import type { QueryKey, UseMutationOptions, UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  IHorseVariant,
  Option,
  PaginatedResponse,
  SalesTrendsTotalsAndMax,
  ChildBundleHorseVariant,
  Bundle,
  IPurchaseOrder,
  IPurchaseOrderLineItem,
  QueryParams,
  HomeRecommendationsResponse,
  ShopifyVariant,
  HorseLocation,
  LinkedLocation,
  Supplier,
  SupplierHorseVariant,
  Vendor,
  VendorHorseVariantRow,
  TransferOrder,
  TransferOrderLineItem,
  SalesTrendsQueryParams,
  NumberRangeFilter,
  CostAdjustment,
  InventoryLevelHistory,
  IndexPurchaseOrder,
  IndexTransferOrder,
  ISupplierSearchOption,
  CreatePurchaseOrder,
  User,
  UpdateUser,
  Banner,
  PurchaseOrderLineItemsQueryParams,
  BaseQueryParams,
  HomeQueryParams,
  RecommendationQueryParams,
  HorseVariantsQueryParams,
  CombineStore,
  PurchaseOrderUpdatePayload,
  PostComment,
  Comment,
  HorseInventoryLevelUpdatePayload,
  IHorseInventoryLevel,
  IShopifyShop,
} from "./types";
import { ShopifyShop } from "./types";
import { vendorPath } from "../helper_functions/utils";

export const useComments = (purchaseOrderId: number): UseQueryResult<Comment[]> => {
  return useQuery<Comment[]>({
    queryKey: ["comments", purchaseOrderId],
    queryFn: async () => {
      const response = await fetch(`/purchase_orders/${purchaseOrderId}/purchase_order_comments`);
      if (!response.ok) {
        throw new Error("Failed to fetch comments");
      }
      return response.json();
    },
  });
};

export const useUpdateCommentMutation = (
  options: UseMutationOptions<Comment, Error, { purchaseOrderId: number; commentId: number; content: string }>,
): UseMutationResult<Comment, Error, { purchaseOrderId: number; commentId: number; content: string }> => {
  return useMutation({
    ...options,
    mutationFn: async ({
      purchaseOrderId,
      commentId,
      content,
    }: {
      purchaseOrderId: number;
      commentId: number;
      content: string;
    }) => {
      return await patch<Comment>(`/purchase_orders/${purchaseOrderId}/purchase_order_comments/${commentId}`, {
        content,
      });
    },
  });
};

export const deleteComment = async (purchaseOrderId: number, commentId: number): Promise<{ message: string }> => {
  return await deleteRequest(`/purchase_orders/${purchaseOrderId}/purchase_order_comments/${commentId}`);
};

export const usePostCommentMutation = (): UseMutationResult<
  Comment,
  Error,
  { purchaseOrderId: number; payload: PostComment }
> => {
  return useMutation({
    mutationFn: async ({ purchaseOrderId, payload }: { purchaseOrderId: number; payload: PostComment }) => {
      return await post<Comment>(`/purchase_orders/${purchaseOrderId}/purchase_order_comments`, {
        comment: payload,
      });
    },
  });
};

// /horse_inventory_levels
export const updateHorseInventoryLevels = async ({
  horse_variant_id,
  horse_inventory_levels,
  tracked,
  reason,
  comment,
}: HorseInventoryLevelUpdatePayload): Promise<HorseInventoryLevel[]> => {
  return await patch<IHorseInventoryLevel[]>(`/horse_variants/${horse_variant_id}/horse_inventory_levels`, {
    horse_variant: {
      tracked,
    },
    horse_inventory_levels,
    reason,
    comment,
  }).then((hils: IHorseInventoryLevel[]): HorseInventoryLevel[] => {
    return hils.map((hil) => {
      return new HorseInventoryLevel(hil);
    });
  });
};

export class HorseInventoryLevel implements IHorseInventoryLevel {
  id?: number;
  horse_location_id: number;
  horse_variant_id: number;
  available: number;
  incoming: number;
  committed: number;
  reserved: number;
  damaged: number;
  safety_stock: number;
  quality_control: number;
  on_hand: number;
  horse_location: { id: number; name: string };

  constructor(hil: IHorseInventoryLevel) {
    Object.assign(this, hil);
  }

  public get updateOrCreateId(): string {
    return `${this.horse_location_id}-${this.horse_variant_id}`;
  }
}

export const useHorseInventoryLevels = (
  queryParams: QueryParams,
  useQueryOptions: { enabled: boolean } = { enabled: true },
): UseQueryResult<HorseInventoryLevel[]> => {
  return useQuery({
    ...useQueryOptions,
    queryKey: ["horse_inventory_levels", queryParams],
    queryFn: async ({ signal }) =>
      await get<HorseInventoryLevel[]>("/horse_inventory_levels.json", queryParams, { signal }).then((hils) => {
        return hils.map((hil) => {
          return new HorseInventoryLevel(hil);
        });
      }),
    placeholderData: [],
  });
};

// /horse_locations
export const useHorseLocationsOptions = ({
  collection,
  omitAll = false,
}: {
  collection?: string;
  omitAll?: boolean;
} = {}): UseQueryResult<Option[]> => {
  const initialOptions = omitAll ? [] : [allLocationsOption];
  return useQuery({
    queryKey: ["horse_locations", collection, omitAll],
    queryFn: async ({ signal }) =>
      await get<PaginatedResponse<HorseLocation>>(
        "/horse_locations.json",
        { collection, totals: false },
        { signal },
      ).then(({ rows }) => {
        return initialOptions.concat(
          rows.map((horseLocation) => ({
            label: horseLocation.name,
            value: horseLocation.id.toString(),
            key: horseLocation.id.toString(),
          })),
        );
      }),
    placeholderData: initialOptions,
  });
};

type HorseLocationsIndex = PaginatedResponse<HorseLocation> & {
  totals: {
    cost_value: number;
    retail_value: number;
  };
};

export const getHorseLocations = async (queryParams: QueryParams): Promise<HorseLocationsIndex> => {
  return await get<HorseLocationsIndex>("/horse_locations.json", queryParams);
};

export const getHorseLocationOptions = async (queryParams: QueryParams): Promise<Option[]> => {
  return await get<{ id: number; name: string }[]>("/horse_locations/search_dropdown.json", queryParams).then(
    (horseLocations) => {
      return horseLocations.map((horseLocation) => ({
        value: horseLocation.id.toString(),
        label: horseLocation.name,
        key: horseLocation.id.toString(),
      }));
    },
  );
};

export const getHorseLocation = async (
  horseLocationId: number,
): Promise<{
  imageUrl: string;
  horseLocation: HorseLocation;
  linkedLocations: LinkedLocation[];
}> => {
  return await get(`/horse_locations/${horseLocationId}.json`);
};

export const getHomeRecommendations = async (queryParams: HomeQueryParams): Promise<HomeRecommendationsResponse> => {
  return await get("/home.json", queryParams);
};

export const allLocations = "All locations";
const allLocationsOption: Option = { label: allLocations, value: allLocations, key: allLocations };
export const allSuppliers = "All suppliers";
const allSuppliersOption: Option = { label: allSuppliers, value: allSuppliers, key: allSuppliers };
export const allTypes = "All types";
const allTypesOption: Option = { label: allTypes, value: allTypes, key: allTypes };
export const allVendors = "All vendors";
const allVendorsOption: Option = { label: allVendors, value: allVendors, key: allVendors };
export const allDiscounted = "All";

// /vendors
export const useHorseVariantVendorsOptions = ({ collection = "undiscarded", omitAll = false } = {}): UseQueryResult<
  Option[]
> => {
  const initialOptions = omitAll ? [] : [allVendorsOption];
  return useQuery({
    queryKey: ["vendors", collection, omitAll],
    queryFn: async ({ signal }) =>
      await get<{ vendors: string[] }>("/vendors.json", { collection }, { signal }).then(({ vendors }) => {
        return initialOptions.concat(vendors.map((vendor) => ({ label: vendor, value: vendor, key: vendor })));
      }).catch((error: unknown) => {
        console.error("Error fetching vendors:", error);
        return initialOptions;
      }),
    placeholderData: initialOptions,
  });
};

// /tags
export const useHorseVariantTagsOptions = (search?: string): UseQueryResult<Option[]> => {
  return useQuery({
    queryKey: ["useHorseVariantTagsOptions", search || ""],
    queryFn: async ({ signal }) => await get<string[]>("/tags.json", { search }, { signal }).then((tags): Option[] => {
      return tags.map((tag) => ({
          label: tag,
          value: tag,
          key: tag,
        }));
    }),
    placeholderData: [],
  });
};

export const getHorseVariantsVendors = async (collection?: string): Promise<{ vendors: string[] }> => {
  return await get("/vendors.json", { collection });
};

export const getVendor = async (
  vendorIdOrName: string,
): Promise<{
  vendor: Vendor;
  canonical_path: string;
}> => {
  return await get(`${vendorPath(vendorIdOrName)}.json`);
};

export const updateVendor = async (vendorId: number, payload: object): Promise<{ vendor: Vendor }> => {
  return await patch(`/vendors/${vendorId}.json`, payload);
};

export const getVendorHorseVariants = async (
  vendorId: number,
  queryParams: QueryParams,
): Promise<PaginatedResponse<VendorHorseVariantRow>> => {
  return await get(`/vendors/${vendorId}/vendor_horse_variants.json`, queryParams);
};

// /horse_variants
export const useHorseVariants = (
  queryParams: HorseVariantsQueryParams = {},
): UseQueryResult<PaginatedResponse<IHorseVariant & { value: number }>> => {
  return useQuery({
    queryKey: ["horse_variants", queryParams],
    queryFn: async ({ signal }) =>
      await get<PaginatedResponse<IHorseVariant & { value: number }>, HorseVariantsQueryParams>(
        "/horse_variants.json",
        queryParams,
        { signal },
      ),
    placeholderData: { rows: [], hasNext: false, hasPrev: false },
  });
};

export const useHorseVariantsProductTypeOptions = (queryParams = {}): UseQueryResult<Option[]> => {
  const initialOptions = [allTypesOption];
  return useQuery({
    queryKey: ["horse_variants/product_types", queryParams],
    queryFn: async ({ signal }) =>
      await get<{ product_types: string[] }>("/horse_variants/product_types.json", queryParams, { signal }).then(
        ({ product_types }) => {
          return initialOptions.concat(
            product_types.map((productType) => ({ label: productType, value: productType, key: productType })),
          );
        },
      ),
    placeholderData: initialOptions,
  });
};

export const getHorseVariant = async (
  horseVariantId: number,
): Promise<{
  horseVariant: IHorseVariant;
  shopifyVariants: ShopifyVariant[];
  availability: {
    shopify_domain: string;
    friendly_name: string;
    available: boolean;
  }[];
}> => {
  return await get(`/horse_variants/${horseVariantId}.json`);
};

export const useHorseVariant = (
  horseVariantId: number | undefined,
): UseQueryResult<{
  horseVariant: IHorseVariant;
  shopifyVariants: ShopifyVariant[];
  availability: {
    shopify_domain: string;
    friendly_name: string;
    available: boolean;
  }[];
}> => {
  return useQuery({
    queryKey: ["horse_variants", horseVariantId],
    queryFn: () => horseVariantId ? getHorseVariant(horseVariantId) : undefined,
    enabled: !!horseVariantId,
  });
};

export const updateHorseVariant = async (id: number, payload: object): Promise<IHorseVariant> => {
  return await patch(`/horse_variants/${id}`, payload);
};

export const importHorseVariants = async (
  payload: object,
): Promise<{
  errors?: string[];
  message?: string;
}> => {
  return await post("/horse_variants/import", payload);
};

export const useHorseVariantsTotals = (
  queryParams: QueryParams,
): UseQueryResult<{
  currently_available_quantity: number;
  committed_quantity: number;
  total_value: number;
}> => {
  return useQuery({
    queryKey: ["horse_variants/totals", queryParams],
    queryFn: async ({ signal }) => await get("/horse_variants/totals.json", queryParams, { signal }),
    placeholderData: { currently_available_quantity: 0, committed_quantity: 0, total_value: 0 },
  });
};

// /inventory_level_histories
export const useInventoryLevelHistories = (
  queryParams: QueryParams,
): UseQueryResult<PaginatedResponse<InventoryLevelHistory>> => {
  return useQuery({
    queryKey: ["inventory_level_histories", queryParams],
    queryFn: async ({ signal }) => await get("/inventory_level_histories.json", queryParams, { signal }),
    placeholderData: { rows: [], hasNext: false, hasPrev: false },
  });
};

// /cost_adjustments
export const useCostAdjustments = (
  purchase_order_id: number,
): UseQueryResult<{ cost_adjustments: CostAdjustment[] }> => {
  return useQuery({
    queryKey: ["cost_adjustments", purchase_order_id],
    queryFn: async ({ signal }) => await get(`/purchase_orders/${purchase_order_id}/cost_adjustments.json`, undefined, { signal }),
    placeholderData: { cost_adjustments: [] },
  });
};

export const useSaveCostAdjustmentsMutation = (): UseMutationResult<
  { cost_adjustments: CostAdjustment[] },
  Error,
  { purchase_order_id: number; cost_adjustments: CostAdjustment[] }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ purchase_order_id, cost_adjustments }: {
      purchase_order_id: number;
      cost_adjustments: CostAdjustment[];
    }) => {
      return patch(`/purchase_orders/${purchase_order_id}/cost_adjustments`, { cost_adjustments });
    },
    onSuccess: (response, variables) => {
      queryClient.setQueryData(["cost_adjustments", variables.purchase_order_id], response);
    },
  });
};

// /purchase_orders
export const getPurchaseOrders = async (
  queryParams: QueryParams,
): Promise<
  PaginatedResponse<IndexPurchaseOrder>
> => {
  return await get(`/purchase_orders`, queryParams);
};

export const usePurchaseOrdersTotals = (
  queryParams: QueryParams,
): UseQueryResult<{
  total_cost: number;
  quantity: number;
  received: number;
}> => {
  return useQuery({
    queryKey: ["purchase_orders/totals", queryParams],
    queryFn: async ({ signal }) => await get(`/purchase_orders/totals.json`, queryParams, { signal }),
    placeholderData: {
      total_cost: 0,
      quantity: 0,
      received: 0,
    },
  });
};

export const usePurchaseOrder = (
  purchaseOrderId: number | undefined,
): UseQueryResult<{
  purchaseOrder: IPurchaseOrder;
}> => {
  return useQuery({
    queryKey: ["purchase_orders", purchaseOrderId],
    queryFn: ({ signal }) => get(`/purchase_orders/${purchaseOrderId}.json`, undefined, { signal }),
    enabled: !!purchaseOrderId,
  });
};

export const createPurchaseOrder = async (payload: {
  purchase_order: CreatePurchaseOrder;
}): Promise<IPurchaseOrder> => {
  return await post("/purchase_orders.json", payload);
};

export const deletePurchaseOrder = async (id: number): Promise<any> => {
  return await deleteRequest(`/purchase_orders/${id}`);
};

export const updatePurchaseOrderSelection = async (
  purchase_order_id: number,
  payload: object,
  queryParams: QueryParams,
): Promise<{ horse_variant_ids: number[] }> => {
  return await patch(`/purchase_orders/${purchase_order_id}/update_selection`, payload, queryParams);
};

export const updatePurchaseOrder = async (
  purchase_order_id: number,
  payload: { purchase_order: PurchaseOrderUpdatePayload },
): Promise<IPurchaseOrder> => {
  return await patch(`/purchase_orders/${purchase_order_id}.json`, payload);
};

export const getExchangeRate = async (from: string, to: string): Promise<{ rate: number }> => {
  return await get<{ rate: number }>("/exchange_rates.json", { from, to });
};

export const removeAllPurchaseOrderHorseVariantLineItem = async (purchase_order_id: number): Promise<any> => {
  return await post(`/purchase_orders/${purchase_order_id}/destroy_all_line_items`);
};

export const getRecommendations = async (
  purchase_order_id: number,
  queryParams: RecommendationQueryParams,
): Promise<IPurchaseOrderLineItem[]> => {
  return await get<IPurchaseOrderLineItem[], RecommendationQueryParams>(
    `/purchase_orders/${purchase_order_id}/recommend_horse_variants.json`,
    queryParams,
  );
};

/**
 * @param purchaseOrderId
 * @param payload
 * @param queryParams Used for preserving the sort-order when emailing a PDF
 * @returns
 */
export const emailPdf = (
  purchaseOrderId: number,
  payload: {
    subject: string;
    to: string;
    reply_to: string;
    body: string;
  },
  queryParams: QueryParams = {},
): Promise<void> => {
  const urlSearchParams = processQueryParams(queryParams);
  return post(`/purchase_orders/${purchaseOrderId}/email_pdf?${urlSearchParams.toString()}`, payload);
};

export const getPurchaseOrderHorseVariants = async (
  purchaseOrderId: number,
  queryParams: PurchaseOrderLineItemsQueryParams = {},
): Promise<IPurchaseOrderLineItem[]> => {
  return await get<IPurchaseOrderLineItem[], PurchaseOrderLineItemsQueryParams>(
    `/purchase_orders/${purchaseOrderId}/purchase_order_line_items.json`,
    queryParams,
  );
};

export const usePurchaseOrderHorseVariantSelectedIds = (
  purchaseOrderId: number,
  useQueryOptions: { enabled: boolean, queryKey?: QueryKey } = { enabled: true, queryKey: undefined },
): UseQueryResult<{ horse_variant_id: number }[]> => {
  return useQuery({
    queryKey: useQueryOptions.queryKey || ["purchase_order_line_items_selected_ids", purchaseOrderId],
    queryFn: async ({ signal }) => await get<{ horse_variant_id: number }[]>(
      `/purchase_orders/${purchaseOrderId}/purchase_order_line_items/selected_ids.json`,
      undefined,
      { signal },
    ),
    placeholderData: [],
    ...useQueryOptions,
  });
};

export const importPurchaseOrderLineItems = async (
  purchaseOrderId: number,
  payload: object,
): Promise<{
  data: IPurchaseOrderLineItem[];
  errors?: string[];
  message?: string;
}> => {
  return await post(`/purchase_orders/${purchaseOrderId}/purchase_order_line_items/import`, payload);
};

export const removePurchaseOrderHorseVariantLineItem = async (
  purchase_order_horse_variant_id: number,
): Promise<any> => {
  return await deleteRequest(`/purchase_order_line_items/${purchase_order_horse_variant_id}`);
};

// /read_banners
export const getReadBanners = async (): Promise<any> => {
  return await get("/read_banners.json");
};

export const getReadBanner = async (banner_name: string): Promise<any> => {
  return await get(`/read_banners/${banner_name}.json`);
};

export const createReadBanner = async (banner_name: string): Promise<any> => {
  return await post("/read_banners.json", { read_banners: { banner_name } });
};

export function forceNumber(value: number | string | undefined): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value === "") {
    return undefined;
  }
  return typeof value === "string" ? parseFloat(value) : value;
}

// /sales_trends
export const useSalesTrends = (
  queryParams: SalesTrendsQueryParams = {},
): UseQueryResult<PaginatedResponse<IHorseVariant>> => {
  return useQuery({
    queryKey: ["sales_trends", queryParams],
    queryFn: async ({ signal }) =>
      await get<PaginatedResponse<IHorseVariant>, SalesTrendsQueryParams>("/sales_trends.json", queryParams, {
        signal,
      }),
  });
};

export const useTotalsAndMax = (queryParams: SalesTrendsQueryParams = {}): UseQueryResult<SalesTrendsTotalsAndMax> => {
  return useQuery({
    queryKey: ["sales_trends/totals_and_max", queryParams],
    queryFn: async ({ signal }) => await get("/sales_trends/totals_and_max.json", queryParams, { signal }),
    placeholderData: {
      total_sales_rate: 0,
      total_sales: 0,
      max_sales_rate: 1.9,
      total_ordered: 0,
      total_available: 0,
      total_revenue: 0,
    },
  });
};

// shopify_shops
export const useShopifyShop = (): UseQueryResult<ShopifyShop> => {
  return useQuery({
    queryKey: ["shopify_shop"],
    queryFn: async ({ signal }) =>
      await get<IShopifyShop>("/shopify_shop.json", undefined, { signal }).then(
        (iShopifyShop) => new ShopifyShop(iShopifyShop),
      ),
    placeholderData: new ShopifyShop({
      name: "",
      email: "",
      shopify_domain: "",
      import_step: "imported",
      readyForRecommendations: true,
    }),
  });
};

// currencies
export const useCurrencies = (): UseQueryResult<string[]> => {
  return useQuery({
    queryKey: ["currencies"],
    queryFn: async ({ signal }) => await get<string[]>("/currencies.json", undefined, { signal }),
    placeholderData: [],
  });
};

export interface ChoiceLike {
  helpText: string,
  label: string,
  value: string,
}

// /shopify_channels
// Used for Published filtering
export const useShopifyChannelAvailabilityOptions = (): UseQueryResult<ChoiceLike[]> => {
  return useQuery({
    queryKey: ["useShopifyChannelAvailabilityOptions"],
    queryFn: async ({ signal }) =>
      await get<{ shopify_channels: { id: string; friendly_name: string }[] }>("/shopify_channels.json", undefined, {
        signal,
      }).then(({ shopify_channels }) => {
        return formatShopifyChannels(shopify_channels);
      }),
    placeholderData: [],
  });
};

// Format friendly_shopify_channels from application_helper.rb
// Horse specific
function formatShopifyChannels(shopifyChannels: { id: string; friendly_name: string }[]): ChoiceLike[] {
  return shopifyChannels.flatMap((shopifyChannel) => {
    const [channelName, storeName] = shopifyChannel.friendly_name.split("-");
    const available = `Available on ${channelName}`;
    const unAvailable = `Unavailable on ${channelName}`;
    return [
      {
        helpText: storeName,
        label: available,
        value: `${shopifyChannel.id}:true`,
      },
      {
        helpText: storeName,
        label: unAvailable,
        value: `${shopifyChannel.id}:false`,
      },
    ] satisfies ChoiceLike[];
  });
}

// Used for sales filtering
export const useShopifyChannelSalesOptions = (): UseQueryResult<Option[]> => {
  return useQuery({
    queryKey: ["useShopifyChannelSalesOptions"],
    queryFn: async ({ signal }) =>
      await get("/shopify_channels.json", undefined, { signal }).then(
        ({
          shopify_channels,
        }: {
          shopify_channels: {
            id: number;
            friendly_name: string;
          }[];
        }): Option[] => {
          return shopify_channels.map((shopifyChannel) => {
            return {
              label: shopifyChannel.friendly_name,
              value: shopifyChannel.id.toString(),
              key: shopifyChannel.id.toString(),
            };
          });
        },
      ),
    placeholderData: [],
  });
};

export const useBanners = (): UseQueryResult<Banner> => {
  return useQuery({
    queryKey: ["banners"],
    queryFn: async ({ signal }) => await get(`/banners.json`, undefined, { signal }),
    placeholderData: {},
  });
};

export const getSuppliers = async (
  queryParams: QueryParams,
): Promise<PaginatedResponse<{ id: number; name: string }>> => {
  return await get("/suppliers.json", queryParams);
};

export const useSuppliersSearchOptions = ({ omitAll = false } = {}): UseQueryResult<Option[]> => {
  const initialOptions = omitAll ? [] : [allSuppliersOption];
  return useQuery({
    queryKey: ["suppliers", omitAll],
    queryFn: async ({ signal }) =>
      await get<ISupplierSearchOption[]>(`/suppliers/search_dropdown.json`, undefined, { signal }).then(
        (supplierOptions) => {
          return initialOptions.concat(
            supplierOptions.map((sup) => ({ label: sup.name, value: sup.id.toString(), key: sup.id.toString() })),
          );
        },
      ),
    placeholderData: initialOptions,
  });
};

export const getSupplier = async (
  supplierId: number,
): Promise<{
  supplier: Supplier;
  supplierHorseVariants: SupplierHorseVariant[];
  currencies: string[];
}> => {
  return await get(`/suppliers/${supplierId}.json`);
};

export const createSupplier = async (payload: object): Promise<Supplier> => {
  return await post("/suppliers.json", payload);
};

export const deleteSupplier = async (supplierId: number): Promise<any> => {
  return await deleteRequest(`/suppliers/${supplierId}`);
};

export const updateSupplierSelection = async (
  supplierId: number,
  payload: object,
  queryParams: QueryParams,
): Promise<{ horse_variant_ids: number[] }> => {
  return await patch(`/suppliers/${supplierId}/update_selection`, payload, queryParams);
};

export const updateSupplier = async (supplierId: number, payload: object): Promise<{ supplier: Supplier }> => {
  return await patch(`/suppliers/${supplierId}.json`, payload);
};

export const importSupplierHorseVariants = async (
  supplierId: number,
  payload: object,
): Promise<{
  data?: SupplierHorseVariant[];
  errors?: string[];
  message?: string;
}> => {
  return await post(`/suppliers/${supplierId}/supplier_horse_variants/import`, payload);
};

export const getSupplierHorseVariants = async (
  supplierId: number,
  queryParams: QueryParams,
): Promise<PaginatedResponse<SupplierHorseVariant>> => {
  return await get(`/suppliers/${supplierId}/supplier_horse_variants.json`, queryParams);
};

export const useSupplierHorseVariantSelectedIds = (
  supplierId: number,
  useQueryOptions: { enabled: boolean, queryKey?: QueryKey } = { enabled: true, queryKey: undefined },
): UseQueryResult<{ horse_variant_id: number }[]> => {
  return useQuery({
    queryKey: useQueryOptions.queryKey || ["supplier_horse_variant_selected_ids", supplierId],
    queryFn: async ({ signal }) => await get(`/suppliers/${supplierId}/supplier_horse_variants/selected_ids.json`, undefined, { signal }),
    placeholderData: [],
    ...useQueryOptions,
  });
};

export const removeAllSupplierHorseVariants = async (supplierId: number): Promise<any> => {
  return await post(`/suppliers/${supplierId}/destroy_all_line_items`);
};

// /supplier_horse_variants
export const removeSupplierHorseVariant = async (supplierHorseVariantId: number): Promise<any> => {
  return await deleteRequest(`/supplier_horse_variants/${supplierHorseVariantId}`);
};

// /transfer_orders
export const useTransferOrders = (
  queryParams: QueryParams,
): UseQueryResult<PaginatedResponse<IndexTransferOrder> & { total_quantity_sent: number; total_value: number }> => {
  return useQuery({
    queryKey: ["transfer_orders", queryParams],
    queryFn: async ({ signal }) => await get(`/transfer_orders.json`, queryParams, { signal }),
    placeholderData: { rows: [], hasNext: false, hasPrev: false, total_quantity_sent: 0, total_value: 0 },
  });
};

export const useTransferOrder = (
  transferOrderId: number,
): UseQueryResult<{
  origin: HorseLocation;
  destination: HorseLocation;
  transferOrder: TransferOrder;
  lineItems?: TransferOrderLineItem[];
}> => {
  return useQuery({
    queryKey: ["transfer_order", transferOrderId],
    queryFn: async ({ signal }) => await get(`/transfer_orders/${transferOrderId}.json`, undefined, { signal }),
    placeholderData: { origin: undefined, destination: undefined, transferOrder: undefined, lineItems: undefined },
  });
};

export const deleteTransferOrder = async (id: number): Promise<any> => {
  return await deleteRequest(`/transfer_orders/${id}`);
};

export const updateTransferOrderSelection = async (
  transfer_order_id: number,
  payload: object,
  queryParams: QueryParams,
): Promise<{ horse_variant_ids: number[] }> => {
  return await patch(`/transfer_orders/${transfer_order_id}/update_selection`, payload, queryParams);
};

export const updateTransferOrder = async (transfer_order_id: number, payload: object): Promise<TransferOrder> => {
  return await patch(`/transfer_orders/${transfer_order_id}.json`, payload);
};

export const createTransferOrder = async (payload: object): Promise<TransferOrder> => {
  return await post("/transfer_orders.json", payload);
};

export const removeAllTransferOrderHorseVariantLineItem = async (transfer_order_id: number): Promise<any> => {
  return await post(`/transfer_orders/${transfer_order_id}/destroy_all_line_items`);
};

export const importTransferOrderLineItems = async (
  transferOrderId: number,
  payload: object,
): Promise<{
  data?: TransferOrderLineItem[];
  errors?: string[];
  message?: string;
}> => {
  return await post(`/transfer_orders/${transferOrderId}/transfer_order_horse_variants/import`, payload);
};

export const getTransferOrderHorseVariants = async (transfer_order_id: number): Promise<TransferOrderLineItem[]> => {
  return await get(`/transfer_orders/${transfer_order_id}/transfer_order_horse_variants.json`);
};

export const useTransferOrderHorseVariantSelectedIds = (
  transferOrderId: number,
  useQueryOptions: { enabled: boolean, queryKey?: QueryKey } = { enabled: true, queryKey: undefined },
): UseQueryResult<{ horse_variant_id: number }[]> => {
  return useQuery({
    queryKey: useQueryOptions.queryKey || ["transfer_order_horse_variants_selected_ids", transferOrderId],
    queryFn: async ({ signal }) => await get(`/transfer_orders/${transferOrderId}/transfer_order_horse_variants/selected_ids.json`, undefined, { signal }),
    placeholderData: [],
    ...useQueryOptions,
  });
};

// /transfer_order_horse_variants
export const removeTransferOrderHorseVariantLineItem = async (
  transfer_order_horse_variant_id: number,
): Promise<any> => {
  return await deleteRequest(`/transfer_order_horse_variants/${transfer_order_horse_variant_id}`);
};

export const useUser = (): UseQueryResult<{ user: User }> => {
  return useQuery({
    queryKey: ["user"],
    queryFn: async ({ signal }) => await get(`/user.json`, undefined, { signal }),
    placeholderData: { user: { currency: "USD", syncbackSetting: undefined } },
  });
};

export const updateUser = async (currentUserId: number, payload: Partial<UpdateUser>): Promise<{ user: User }> => {
  return await patch(`/users/${currentUserId}`, { user: payload });
};

export const uploadLogo = async (
  currentUserId: number,
  payload: FormData,
): Promise<{
  brandLogo: string;
}> => {
  return await post(`/users/${currentUserId}/upload_logo`, payload);
};

export const saveCombineStore = async (payload: object): Promise<CombineStore> => {
  return await post("/combine_stores", payload);
};

export const useCombineStores = (): UseQueryResult<CombineStore[]> => {
  return useQuery({
    queryKey: ["combine_stores"],
    queryFn: async ({ signal }) => await get<CombineStore[]>(`/combine_stores.json`, undefined, { signal }),
    placeholderData: [],
  });
};

export const useMutateCombineStore = (): UseMutationResult => {
  return useMutation({
    mutationFn: async (combineStoreId: number) => await deleteRequest(`/combine_stores/${combineStoreId}`),
  });
};

// bundles
export const useBundles = (
  queryParams: QueryParams,
  queryObserverOptions: {
    refetchOnMount: boolean | "always";
  },
): UseQueryResult<PaginatedResponse<Bundle>> => {
  return useQuery({
    ...queryObserverOptions,
    queryKey: ["bundles", queryParams],
    queryFn: async ({ signal }) => await get(`/bundles.json`, queryParams, { signal }),
    placeholderData: { rows: [], hasNext: false, hasPrev: false },
  });
};

export const createBundle = async (payload: object): Promise<Bundle> => {
  return await post("/bundles.json", payload);
};

export const deleteBundle = async (bundleId: number): Promise<void> => {
  await deleteRequest(`/bundles/${bundleId}`);
};

export const importBundles = async (
  payload: object,
): Promise<{
  errors?: string[];
  message?: string;
}> => {
  return await post("/bundles/import", payload);
};

export const getBundle = async (
  bundleId: number,
): Promise<{ bundle: Bundle; bundleHorseVariants: ChildBundleHorseVariant[] }> => {
  return await get(`/bundles/${bundleId}.json`);
};

export const updateBundle = async (bundleId: number, payload: object): Promise<Bundle> => {
  return await patch(`/bundles/${bundleId}.json`, payload);
};

export const updateBundleSelection = async (
  bundleId: number,
  payload: object,
  queryParams: QueryParams,
): Promise<{ horse_variant_ids: number[]; errors?: string[] }> => {
  return await patch(`/bundles/${bundleId}/update_selection`, payload, queryParams);
};

export const getBundleHorseVariants = async (bundleId: number): Promise<ChildBundleHorseVariant[]> => {
  return await get(`/bundles/${bundleId}/bundle_horse_variants.json`);
};

export const useBundleHorseVariantSelectedIds = (
  bundleId: number,
  useQueryOptions: { enabled: boolean, queryKey?: QueryKey } = { enabled: true, queryKey: undefined },
): UseQueryResult<{ horse_variant_id: number }[]> => {
  return useQuery({
    queryKey: useQueryOptions.queryKey || ["bundle_horse_variant_selected_ids", bundleId],
    queryFn: async ({ signal }) => await get(`/bundles/${bundleId}/bundle_horse_variants/selected_ids.json`, undefined, { signal }),
    placeholderData: [],
    ...useQueryOptions,
  });
};

export const removeBundleHorseVariant = async (bundleHorseVariantId: number): Promise<{ id: number }> => {
  return await deleteRequest(`/bundle_horse_variants/${bundleHorseVariantId}`);
};

export const removeAllBundleHorseVariants = async (bundleId: number): Promise<{ horse_variant_ids: number[] }> => {
  return await post(`/bundles/${bundleId}/destroy_all_line_items`);
};

export const useSettings = (): UseQueryResult<{
  currentUser: User;
  currencies: string[];
  shopifyShops: ShopifyShop[];
}> => {
  return useQuery({
    queryKey: ["/settings"],
    queryFn: async ({ signal }) => await get("/settings", undefined, { signal }),
    placeholderData: { currentUser: undefined, currencies: undefined, shopify_shops: undefined },
  });
};

const patch = async <ReturnType>(path: string, payload: object, queryParams = {}): Promise<ReturnType> => {
  const urlSearchParams = processQueryParams(queryParams);
  const request = new Request(`${path}?${urlSearchParams.toString()}`, {
    method: "PATCH",
    headers: {
      ...(await getAuthorizationHeaders()),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
    redirect: "error",
  });
  let response: Response;
  try {
    response = await fetch(request);
  } catch (error: unknown) {
    if (shouldReportError(error)) {
      Rollbar.error(error || new Error("PATCH error"), request);
    }
    throw new Error(`Failed to update ${path}`);
  }

  return handleResponse<ReturnType>(response);
};

const get = async <T, P extends BaseQueryParams = QueryParams>(
  path: string,
  queryParams: QueryParams<P> = {} as P,
  { signal }: { signal?: AbortSignal } = {},
): Promise<T> => {
  const urlSearchParams = processQueryParams(queryParams);
  const request = new Request(`${path}?${urlSearchParams.toString()}`, {
    method: "GET",
    headers: {
      ...(await getAuthorizationHeaders()),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "error",
  });
  let response: Response;
  try {
    response = await fetch(request, { signal });
  } catch (error: unknown) {
    if (shouldReportError(error)) {
      Rollbar.error(error || new Error("GET error"), request);
    }
    throw new Error(`Failed to find ${path}`);
  }
  return handleResponse<T>(response);
};

const post = async <T>(path: string, payload = {}): Promise<T> => {
  const headers: Record<string, string> = await getAuthorizationHeaders();

  if (!(payload instanceof FormData)) {
    headers.Accept = "application/json";
    headers["Content-Type"] = "application/json";
  }

  const request = new Request(path, {
    method: "POST",
    headers,
    body: payload instanceof FormData ? payload : JSON.stringify(payload),
    redirect: "error",
  });
  let response: Response;
  try {
    response = await fetch(request);
  } catch (error: unknown) {
    if (shouldReportError(error)) {
      Rollbar.error(error || new Error("POST error"), request);
    }
    throw new Error(`Failed to create ${path}`);
  }
  return handleResponse<T>(response);
};

const deleteRequest = async <T>(path: string): Promise<T> => {
  const request = new Request(path, {
    method: "DELETE",
    headers: {
      ...(await getAuthorizationHeaders()),
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    redirect: "error",
  });
  let response: Response;
  try {
    response = await fetch(request);
  } catch (error: unknown) {
    if (shouldReportError(error)) {
      Rollbar.error(error || new Error("DELETE error"), request);
    }
    throw new Error(`Failed to delete ${path}`);
  }

  return handleResponse<T>(response);
};

export interface ErrorResponse {
  errors: string[];
}

const handleResponse = async <T>(response: Response): Promise<T | undefined> => {
  if (response.status < 300) {
    try {
      return (await response.json()) as Promise<T>;
    } catch (error: unknown) {
      if (shouldReportError(error)) {
        Rollbar.error(error || new Error("JSON parse error"), response);
      }
      return undefined;
    }
  } else {
    let jsonResponse: { errors: string[] } | undefined;
    try {
      jsonResponse = await response.json();
    } catch (error: unknown) {
      if (shouldReportError(error)) {
        Rollbar.error(error || new Error("JSON parse error"), response);
      }
      throw new Error(`Failed to parse response: ${response.statusText} (${response.status}) from ${response.url}`);
    }
    if (jsonResponse.errors && Object.keys(jsonResponse.errors).length > 0) {
      jsonResponse.errors.forEach((errorMessage) => {
        Rollbar.error(errorMessage ? new Error(errorMessage) : new Error("Empty error message"), response);
      });
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw jsonResponse;
    } else {
      throw new Error(`Failed to parse response: ${response.statusText} (${response.status}) from ${response.url}`);
    }
  }
};

export const processQueryParams = <P extends BaseQueryParams>(queryParams: QueryParams<P>): URLSearchParams => {
  const formattedQueryParams = { ...queryParams };
  const urlSearchParams = new URLSearchParams();
  Object.keys(formattedQueryParams).forEach((key: string) => {
    if (Array.isArray(formattedQueryParams[key])) {
      const values = formattedQueryParams[key] as (Date | boolean | number | string)[];

      if (!key.endsWith("[]")) {
        delete formattedQueryParams[key];
        key = `${key}[]`;
      }

      values.forEach((value) => {
        conditionallyAppend(urlSearchParams, key, value);
      });
    } else if (
      typeof formattedQueryParams[key] === "object" &&
      !(formattedQueryParams[key] instanceof Date) &&
      formattedQueryParams[key] !== null &&
      formattedQueryParams[key] !== undefined &&
      formattedQueryParams[key] !== ""
    ) {
      const values = formattedQueryParams[key] as NumberRangeFilter | { column: string; direction: string };
      delete formattedQueryParams[key];
      Object.keys(values).forEach((subKey) => {
        const subValue = values[subKey] as Date | boolean | number | string;
        conditionallyAppend(urlSearchParams, `${key}[${subKey}]`, subValue);
      });
    } else if (
      formattedQueryParams[key] !== null &&
      formattedQueryParams[key] !== undefined &&
      formattedQueryParams[key] !== ""
    ) {
      conditionallyAppend(urlSearchParams, key, formattedQueryParams[key] as Date | boolean | number | string);
    }
  });
  return urlSearchParams;
};

function conditionallyAppend(
  urlSearchParams: URLSearchParams,
  key: string,
  value: Date | boolean | number | string,
): void {
  if (value !== null && value !== undefined && value !== "") {
    if (value instanceof Date && isNaN(value.getTime())) {
      return;
    }
    urlSearchParams.append(key, formatValue(value));
  }
}

function formatValue(value: Date | boolean | number | string): string {
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  } else if (value instanceof Date) {
    return value.toISOString();
  }
  return value.toString();
}

// HACK: This hack is caused by the need to pass JWT tokens in the HEADERS
// That means we can't open tabs
// So the only solution is to `fetch` the file in the background and the give it to the browser as a blob
// https://stackoverflow.com/questions/29452031/how-to-handle-file-downloads-with-jwt-based-authentication
export const downloadFile = async (filePath: string, filename: string, queryParams?: QueryParams): Promise<void> => {
  const urlSearchParams = processQueryParams(queryParams);

  const anchor = document.createElement("a");
  document.body.appendChild(anchor);
  const headers = new Headers(await getAuthorizationHeaders());

  const path = queryParams ? `${filePath}?${urlSearchParams.toString()}` : filePath;
  const response = await fetch(path, { headers });
  if (response.status >= 300) {
    throw new Error(`Failed to download file: ${response.statusText}`);
  }
  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition") || "";
  let serverFilename = contentDisposition.split("; ").find((property) => property.startsWith("filename"));
  if (serverFilename !== undefined) {
    serverFilename = serverFilename.split("=")[1].replaceAll('"', "");
  }
  const objectUrl = window.URL.createObjectURL(blob);

  anchor.href = objectUrl;
  anchor.download = serverFilename || filename;
  anchor.click();

  window.URL.revokeObjectURL(objectUrl);
};

const getAuthorizationHeaders = async (): Promise<Record<string, string>> => {
  const sessionToken = await shopify.idToken();

  return { Authorization: `Bearer ${sessionToken}` };
};

function shouldReportError(error: unknown): boolean {
  if (error instanceof Error) {
    return (
      !error.message.includes("The user aborted a request") &&
      !error.message.includes("signal is aborted without reason") &&
      !error.message.includes("Fetch is aborted") &&
      !error.message.includes("NetworkError when attempting to fetch resource") &&
      !error.message.includes(`Unexpected token '<', "<!DOCTYPE "... is not valid JSON`) &&
      !error.message.includes("The operation was aborted.") &&
      !error.message.includes("Failed to fetch")
    );
  } else {
    return true;
  }
}

export const getArrayParamValue = <T>(
  values: string[],
  type: "string" | "number" | "boolean" | "date",
  defaultValue?: T[],
): T[] => {
  if (values === null || values === undefined) return defaultValue;

  switch (type) {
    case "string":
      return values.map((value) => getParamValue(value));
    case "number": {
      return values.map((value) => getNumberParamValue(value) as T);
    }
    case "boolean":
      return values.map((value) => getBooleanParamValue(value) as T);
    case "date":
      return values.map((value) => getDateParamValue(value) as T);
    default:
      return defaultValue;
  }
};

export const getParamValue = <T>(value: string, defaultValue: T = undefined): T | undefined => {
  return value === null || value === undefined ? defaultValue : value as T;
};

export const getNumberParamValue = (value: string, defaultValue: number = undefined): number | undefined => {
  if (value === null || value === undefined) return defaultValue;
  const n = Number(value);
  return isNaN(n) ? defaultValue : n;
};

export const getBooleanParamValue = (value: string, defaultValue: boolean = undefined): boolean | undefined => {
  return value === "true" || value === "1" ? true : value === "false" || value === "0" ? false : defaultValue;
};

export const getDateParamValue = (value: string, defaultValue: Date = undefined): Date | undefined => {
  return value ? new Date(value) : defaultValue;
};

interface Collection {
  id: number;
  title: string;
}

export function useCollections(): UseQueryResult<Collection[]> {
  return useQuery<Collection[]>({
    queryKey: ["collections"],
    queryFn: async () => {
      const response = await fetch("/collections");
      if (!response.ok) {
        throw new Error("Failed to fetch collections");
      }
      return response.json() ;
    },
    placeholderData: [],
  });
}
